// SessionRow — single row in OLIVIUM Presence.
// Tighter command-center rhythm to match the rest of the shell.

import type { MouseEvent } from 'react'
import { useShell } from '../shell/ShellContext'
import { SHELL_TOKENS, text, timeAgo } from '../shell/styles'
import type { OlvSession } from '../hooks/types'

interface SessionRowProps {
  session: OlvSession
  onRevoke?: (session: OlvSession) => void
  revoking?: boolean
}

export default function SessionRow({ session, onRevoke, revoking }: SessionRowProps) {
  const { openDetail } = useShell()
  const suspicious = session.status === 'suspicious'
  const accent = suspicious ? SHELL_TOKENS.red : SHELL_TOKENS.green
  const revoked = session.status === 'revoked'

  function handleRevoke(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    if (!onRevoke || revoking || revoked) return
    onRevoke(session)
  }

  return (
    <button
      type="button"
      onClick={() => openDetail(session)}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '11px 14px',
        background: 'transparent',
        color: SHELL_TOKENS.ink,
        border: 'none',
        borderBottom: `1px solid ${SHELL_TOKENS.border}`,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ ...text.rowTitle, fontSize: 12, marginBottom: 2 }}>
          {session.user_id || 'unknown user'}
        </div>
        <div style={text.rowMeta}>
          {session.device_label || 'Unknown device'} · {session.location_hint || session.ip_address || 'Unknown'} · {timeAgo(session.last_seen_at)}
        </div>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 72,
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 8,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            border: `1px solid ${accent}44`,
            background: `${accent}14`,
            color: suspicious ? SHELL_TOKENS.redText : SHELL_TOKENS.greenText,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {session.status || 'active'}
        </div>
        {onRevoke && (
          <button
            type="button"
            onClick={handleRevoke}
            disabled={revoking || revoked}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              border: `1px solid ${SHELL_TOKENS.red}55`,
              background: 'transparent',
              color: revoked ? SHELL_TOKENS.inkFaint : SHELL_TOKENS.redText,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontFamily: 'var(--font-mono)',
              cursor: revoking || revoked ? 'not-allowed' : 'pointer',
              opacity: revoking || revoked ? 0.5 : 1,
            }}
            title={
              revoked
                ? 'Already revoked'
                : 'Revoke this session via /api/admin/revoke-session — audited through OLIVIUM'
            }
          >
            {revoking ? 'Revoking…' : revoked ? 'Revoked' : 'Revoke'}
          </button>
        )}
      </div>
    </button>
  )
}
